import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link, useLocation } from "wouter";
import {
  useGetProject,
  useCompleteTask,
  usePostponeTask,
  useCreateTask,
  useUpdateProject,
  useSuggestNextAction,
  getGetProjectQueryKey,
  getGetTodayQueryKey,
  getListProjectsQueryKey,
  getSuggestNextActionQueryKey,
  type Task,
} from "@workspace/api-client-react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft,
  CheckCircle2,
  Circle,
  Clock,
  Plus,
  Loader2,
  Lightbulb,
  CalendarClock,
  Flag,
  Sparkles,
  Inbox as InboxIcon,
  Calendar as CalendarIcon,
  ScrollText,
  XCircle,
  UserCheck,
  RotateCcw,
  Pencil,
  StickyNote,
  Trash2,
  Paperclip,
  Download,
  Upload,
  FileIcon,
  Mail as MailIcon,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format, formatDistanceToNow } from "date-fns";
import { fr, enUS } from "date-fns/locale";

import TaskEditDialog from "@/components/task-edit-dialog";
import TaskQuickActions, { DelegationBadge } from "@/components/task-quick-actions";

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

// ─── Project Timeline (mixed: decisions + notes) ──────────────────────────────

interface DecisionEntry {
  id: string;
  taskTitle: string | null;
  actionType: string;
  fromValue: string | null;
  toValue: string | null;
  reason: string | null;
  createdAt: string;
}

interface NoteEntry {
  id: string;
  title: string;
  content: string;
  createdAt: string;
}

interface MailEntry {
  id: string;
  subject: string | null;
  fromName: string | null;
  fromEmail: string | null;
  receivedAt: string | null;
}

type TimelineEvent =
  | { kind: "decision"; date: Date; data: DecisionEntry }
  | { kind: "note"; date: Date; data: NoteEntry }
  | { kind: "task"; date: Date; data: { id: string; title: string; status: string; assignedToName?: string | null; delegationNote?: string | null } }
  | { kind: "mail"; date: Date; data: MailEntry };

const DECISION_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  task_refused:      { label: "Refusée",            icon: <XCircle className="w-3.5 h-3.5" />,    color: "text-red-500" },
  task_delegated:    { label: "Déléguée",            icon: <UserCheck className="w-3.5 h-3.5" />,  color: "text-blue-500" },
  task_postponed:    { label: "Reportée",            icon: <Clock className="w-3.5 h-3.5" />,      color: "text-amber-500" },
  task_done:         { label: "Terminée",            icon: <CheckCircle2 className="w-3.5 h-3.5" />, color: "text-emerald-500" },
  task_reactivated:  { label: "Réactivée",           icon: <RotateCcw className="w-3.5 h-3.5" />,  color: "text-violet-500" },
  priority_changed:  { label: "Priorité modifiée",   icon: <Flag className="w-3.5 h-3.5" />,       color: "text-orange-500" },
};

const TASK_STATUS_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  done:      { label: "Terminée",  icon: <CheckCircle2 className="w-3.5 h-3.5" />, color: "text-emerald-500" },
  refused:   { label: "Refusée",   icon: <XCircle className="w-3.5 h-3.5" />,      color: "text-red-500" },
  delegated: { label: "Déléguée",  icon: <UserCheck className="w-3.5 h-3.5" />,   color: "text-blue-500" },
  postponed: { label: "Reportée",  icon: <Clock className="w-3.5 h-3.5" />,        color: "text-amber-500" },
};

function ProjectTimeline({ projectId, tasks }: { projectId: string; tasks: Task[] }) {
  const { i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : fr;

  const { data: decisions = [], isLoading: loadingDecisions } = useQuery<DecisionEntry[]>({
    queryKey: ["project-decision-log", projectId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/decision-log`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      return res.json() as Promise<DecisionEntry[]>;
    },
  });

  const { data: notes = [], isLoading: loadingNotes } = useQuery<NoteEntry[]>({
    queryKey: ["project-notes-timeline", projectId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/notes`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      return res.json() as Promise<NoteEntry[]>;
    },
  });

  const { data: mails = [], isLoading: loadingMails } = useQuery<MailEntry[]>({
    queryKey: ["project-messages", projectId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/messages`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      return res.json() as Promise<MailEntry[]>;
    },
  });

  const isLoading = loadingDecisions || loadingNotes || loadingMails;

  const taskStatusSet = new Set(["done", "refused", "delegated", "postponed"]);
  const taskEvents: TimelineEvent[] = tasks
    .filter((t) => taskStatusSet.has(t.status))
    .map((t): TimelineEvent => ({
      kind: "task",
      date: new Date(t.completedAt ?? t.createdAt),
      data: { id: t.id, title: t.title, status: t.status, assignedToName: t.assignedToName, delegationNote: t.delegationNote },
    }));

  const events: TimelineEvent[] = [
    ...decisions.map((d): TimelineEvent => ({ kind: "decision", date: new Date(d.createdAt), data: d })),
    ...notes.map((n): TimelineEvent => ({ kind: "note", date: new Date(n.createdAt), data: n })),
    ...taskEvents,
    ...mails
      .filter((m) => m.receivedAt)
      .map((m): TimelineEvent => ({ kind: "mail", date: new Date(m.receivedAt!), data: m })),
  ].sort((a, b) => b.date.getTime() - a.date.getTime());

  if (isLoading) return null;
  if (events.length === 0) return null;

  return (
    <section className="pt-6 border-t border-border/50">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <ScrollText className="w-5 h-5 text-primary" />
        Chronologie du projet
      </h2>
      <div className="relative">
        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border/50" />
        <div className="space-y-3 pl-6">
          {events.slice(0, 40).map((ev, idx) => {
            if (ev.kind === "decision") {
              const meta = DECISION_META[ev.data.actionType] ?? {
                label: ev.data.actionType,
                icon: <Clock className="w-3.5 h-3.5" />,
                color: "text-muted-foreground",
              };
              return (
                <div key={`d-${ev.data.id}`} className="relative flex items-start gap-2">
                  <span
                    className="absolute -left-6 top-0.5 w-3.5 h-3.5 rounded-full bg-background border-2 border-border"
                    style={{ transform: "translateX(-1px)" }}
                  />
                  <span className={`shrink-0 mt-0.5 ${meta.color}`}>{meta.icon}</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium">{ev.data.taskTitle ?? "(tâche supprimée)"}</span>
                    <Badge variant="outline" className="ml-2 text-xs py-0">{meta.label}</Badge>
                    {ev.data.toValue && ev.data.actionType === "task_delegated" && (
                      <span className="text-xs text-muted-foreground ml-1.5">→ {ev.data.toValue}</span>
                    )}
                    {ev.data.fromValue && ev.data.toValue && ev.data.actionType === "priority_changed" && (
                      <span className="text-xs text-muted-foreground ml-1.5">{ev.data.fromValue} → {ev.data.toValue}</span>
                    )}
                    {ev.data.reason && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{ev.data.reason}</p>
                    )}
                    <p className="text-xs text-muted-foreground/60 mt-0.5">
                      {formatDistanceToNow(ev.date, { addSuffix: true, locale: dateLocale })}
                    </p>
                  </div>
                </div>
              );
            }

            if (ev.kind === "task") {
              const meta = TASK_STATUS_META[ev.data.status] ?? {
                label: ev.data.status,
                icon: <Circle className="w-3.5 h-3.5" />,
                color: "text-muted-foreground",
              };
              return (
                <div key={`t-${ev.data.id}-${idx}`} className="relative flex items-start gap-2">
                  <span
                    className="absolute -left-6 top-0.5 w-3.5 h-3.5 rounded-full bg-background border-2 border-border"
                    style={{ transform: "translateX(-1px)" }}
                  />
                  <span className={`shrink-0 mt-0.5 ${meta.color}`}>{meta.icon}</span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium">{ev.data.title}</span>
                    <Badge variant="outline" className="ml-2 text-xs py-0">{meta.label}</Badge>
                    {ev.data.status === "delegated" && ev.data.assignedToName && (
                      <span className="text-xs text-muted-foreground ml-1.5">→ {ev.data.assignedToName}</span>
                    )}
                    {ev.data.delegationNote && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{ev.data.delegationNote}</p>
                    )}
                    <p className="text-xs text-muted-foreground/60 mt-0.5">
                      {formatDistanceToNow(ev.date, { addSuffix: true, locale: dateLocale })}
                    </p>
                  </div>
                </div>
              );
            }

            if (ev.kind === "mail") {
              return (
                <div key={`m-${ev.data.id}`} className="relative flex items-start gap-2">
                  <span
                    className="absolute -left-6 top-0.5 w-3.5 h-3.5 rounded-full bg-background border-2 border-border"
                    style={{ transform: "translateX(-1px)" }}
                  />
                  <span className="shrink-0 mt-0.5 text-sky-500">
                    <MailIcon className="w-3.5 h-3.5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium line-clamp-1">{ev.data.subject ?? "(sans objet)"}</span>
                    <Badge variant="outline" className="ml-2 text-xs py-0">Email</Badge>
                    {ev.data.fromName && (
                      <span className="text-xs text-muted-foreground ml-1.5">{ev.data.fromName}</span>
                    )}
                    <p className="text-xs text-muted-foreground/60 mt-0.5">
                      {formatDistanceToNow(ev.date, { addSuffix: true, locale: dateLocale })}
                    </p>
                  </div>
                </div>
              );
            }

            return (
              <div key={`n-${ev.data.id}`} className="relative flex items-start gap-2">
                <span
                  className="absolute -left-6 top-0.5 w-3.5 h-3.5 rounded-full bg-background border-2 border-border"
                  style={{ transform: "translateX(-1px)" }}
                />
                <span className="shrink-0 mt-0.5 text-muted-foreground">
                  <StickyNote className="w-3.5 h-3.5" />
                </span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium">{ev.data.title}</span>
                  <Badge variant="outline" className="ml-2 text-xs py-0">Note</Badge>
                  {ev.data.content && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{ev.data.content}</p>
                  )}
                  <p className="text-xs text-muted-foreground/60 mt-0.5">
                    {formatDistanceToNow(ev.date, { addSuffix: true, locale: dateLocale })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Notes section ────────────────────────────────────────────────────────────

interface NoteItem {
  id: string;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

function NotesSection({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : fr;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [open, setOpen] = useState(false);

  const NOTES_QK = ["project-notes", projectId] as const;

  const { data: notes = [] } = useQuery<NoteItem[]>({
    queryKey: NOTES_QK,
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/notes`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      return res.json() as Promise<NoteItem[]>;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (body: { title: string; content: string }) => {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/notes`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTES_QK });
      setTitle("");
      setContent("");
      setOpen(false);
    },
    onError: () => toast({ title: "Erreur", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/notes/${noteId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("failed");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: NOTES_QK }),
    onError: () => toast({ title: "Erreur", variant: "destructive" }),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    createMutation.mutate({ title: title.trim(), content: content.trim() });
  };

  return (
    <section className="pt-6 border-t border-border/50">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <StickyNote className="w-5 h-5 text-primary" />
          Notes
          {notes.length > 0 && (
            <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded-full font-mono">
              {notes.length}
            </span>
          )}
        </h2>
        <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)}>
          <Plus className="w-3.5 h-3.5 mr-1" />
          Ajouter
        </Button>
      </div>

      {open && (
        <form onSubmit={handleSubmit} className="mb-4 space-y-3 p-4 rounded-xl border border-border bg-secondary/20">
          <input
            className="w-full bg-transparent text-sm font-semibold placeholder:text-muted-foreground focus:outline-none"
            placeholder="Titre de la note…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            required
          />
          <Textarea
            className="resize-none text-sm min-h-[80px] bg-transparent border-0 border-t border-border rounded-none px-0 focus-visible:ring-0"
            placeholder="Contenu (contexte, décisions, observations…)"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={3}
          />
          <div className="flex items-center gap-2 justify-end">
            <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button type="submit" size="sm" disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
              Sauvegarder
            </Button>
          </div>
        </form>
      )}

      {notes.length === 0 && !open ? (
        <p className="text-sm text-muted-foreground italic py-2 px-1">
          Aucune note. Cliquez sur "Ajouter" pour en créer une.
        </p>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div
              key={note.id}
              className="p-4 rounded-xl bg-primary/5 border border-primary/10 group relative"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-sm">{note.title}</h3>
                <button
                  type="button"
                  onClick={() => deleteMutation.mutate(note.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0"
                  aria-label="Supprimer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {note.content && (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-1">
                  {note.content}
                </p>
              )}
              <p className="text-xs text-muted-foreground/50 mt-2">
                {formatDistanceToNow(new Date(note.createdAt), { locale: dateLocale, addSuffix: true })}
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Files section ────────────────────────────────────────────────────────────

interface ProjectFileItem {
  id: string;
  name: string;
  sizeBytes: number | null;
  contentType: string | null;
  objectPath: string;
  createdAt: string;
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function fileIcon(contentType: string | null): string {
  if (!contentType) return "📄";
  if (contentType.startsWith("image/")) return "🖼️";
  if (contentType.startsWith("video/")) return "🎥";
  if (contentType.includes("pdf")) return "📕";
  if (contentType.includes("word") || contentType.includes("document")) return "📝";
  if (contentType.includes("sheet") || contentType.includes("excel")) return "📊";
  if (contentType.includes("zip") || contentType.includes("archive")) return "📦";
  return "📄";
}

function FilesSection({ projectId }: { projectId: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const FILES_QK = ["project-files", projectId] as const;

  const { data: files = [] } = useQuery<ProjectFileItem[]>({
    queryKey: FILES_QK,
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/files`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      return res.json() as Promise<ProjectFileItem[]>;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (fileId: string) => {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/files/${fileId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("failed");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FILES_QK }),
    onError: () => toast({ title: "Erreur lors de la suppression", variant: "destructive" }),
  });

  const handleUpload = async (file: File) => {
    setUploading(true);
    setUploadProgress(10);
    try {
      // Step 1: get presigned URL
      const urlRes = await fetch(`${API_BASE}/api/projects/${projectId}/files/request-url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: file.name,
          size: file.size,
          contentType: file.type || "application/octet-stream",
        }),
      });
      if (!urlRes.ok) throw new Error("Impossible d'obtenir l'URL d'upload");
      const { uploadURL, objectPath } = (await urlRes.json()) as { uploadURL: string; objectPath: string };

      setUploadProgress(30);

      // Step 2: upload directly to GCS
      const uploadRes = await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });
      if (!uploadRes.ok) throw new Error("Échec de l'upload vers le stockage");

      setUploadProgress(80);

      // Step 3: save metadata
      const saveRes = await fetch(`${API_BASE}/api/projects/${projectId}/files`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: file.name,
          sizeBytes: file.size,
          contentType: file.type || null,
          objectPath,
        }),
      });
      if (!saveRes.ok) throw new Error("Impossible de sauvegarder le fichier");

      setUploadProgress(100);
      queryClient.invalidateQueries({ queryKey: FILES_QK });
      toast({ title: `"${file.name}" uploadé avec succès` });
    } catch (err) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Upload échoué",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleUpload(file);
  };

  const handleDownload = (f: ProjectFileItem) => {
    const url = `${API_BASE}/api/storage${f.objectPath}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = f.name;
    a.click();
  };

  return (
    <section className="pt-6 border-t border-border/50">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Paperclip className="w-5 h-5 text-primary" />
          Fichiers joints
          {files.length > 0 && (
            <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded-full font-mono">
              {files.length}
            </span>
          )}
        </h2>
        <Button
          size="sm"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
          ) : (
            <Upload className="w-3.5 h-3.5 mr-1" />
          )}
          {uploading ? `${uploadProgress}%` : "Joindre un fichier"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {files.length === 0 ? (
        <div
          className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          <FileIcon className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            Glissez un fichier ou cliquez pour joindre
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((f) => (
            <div
              key={f.id}
              className="flex items-center gap-3 p-3 rounded-xl border border-border/60 hover:bg-secondary/30 group transition-colors"
            >
              <span className="text-xl shrink-0">{fileIcon(f.contentType)}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{f.name}</p>
                <p className="text-xs text-muted-foreground">
                  {f.sizeBytes ? formatBytes(f.sizeBytes) + " · " : ""}
                  {formatDistanceToNow(new Date(f.createdAt), { locale: fr, addSuffix: true })}
                </p>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => handleDownload(f)}
                >
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-destructive/60 hover:text-destructive"
                  onClick={() => deleteMutation.mutate(f.id)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function parseEmailRawText(rawText: string | null | undefined): {
  fromName: string;
  subject: string;
} | null {
  if (!rawText) return null;
  const lines = rawText.split("\n");
  const senderLine = lines.find((l) => l.startsWith("Email reçu de : "));
  const subjectLine = lines.find((l) => l.startsWith("Objet : "));
  const fromName = senderLine
    ? senderLine.replace("Email reçu de : ", "").replace(/<[^>]+>/, "").trim()
    : "";
  const subject = subjectLine ? subjectLine.replace("Objet : ", "").trim() : "";
  if (!fromName && !subject) return null;
  return { fromName, subject };
}

type ProjectData = {
  id: string;
  name: string;
  description: string | null;
  status: string;
  priority: string | null;
  deadline: string | null;
};

function ProjectEditDialog({
  project,
  open,
  onOpenChange,
}: {
  project: ProjectData;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const update = useUpdateProject();
  const [, navigate] = useLocation();
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? "");
  const [status, setStatus] = useState(project.status);
  const [priority, setPriority] = useState(project.priority ?? "");
  const [deadline, setDeadline] = useState(
    project.deadline ? project.deadline.slice(0, 10) : "",
  );

  useEffect(() => {
    if (open) {
      setName(project.name);
      setDescription(project.description ?? "");
      setStatus(project.status);
      setPriority(project.priority ?? "");
      setDeadline(project.deadline ? project.deadline.slice(0, 10) : "");
      setDeleteConfirm(false);
    }
  }, [open, project]);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/api/projects/${project.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("delete failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
      toast({ title: t("project_detail_page.delete_success_toast") });
      onOpenChange(false);
      navigate("/app/projects");
    },
    onError: () => {
      toast({ title: t("project_detail_page.delete_error_toast"), variant: "destructive" });
    },
  });

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        id: project.id,
        data: {
          name: name.trim() || project.name,
          description: description.trim() || null,
          status,
          priority: priority || null,
          deadline: deadline || null,
        },
      });
      queryClient.invalidateQueries({ queryKey: getGetProjectQueryKey(project.id) });
      queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
      toast({ title: t("project_detail_page.edit_saved_toast") });
      onOpenChange(false);
    } catch {
      toast({ title: t("common.update_error"), variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setDeleteConfirm(false); onOpenChange(v); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("project_detail_page.edit_title")}</DialogTitle>
        </DialogHeader>

        {deleteConfirm ? (
          <div className="py-2 space-y-3">
            <p className="font-semibold text-foreground">
              {t("project_detail_page.delete_confirm_title")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("project_detail_page.delete_confirm_desc")}
            </p>
            <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => setDeleteConfirm(false)}
                className="sm:mr-auto"
                disabled={deleteMutation.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t("project_detail_page.delete_confirm_btn")}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("project_detail_page.edit_name_label")}
                </label>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("project_detail_page.edit_desc_label")}
                </label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("project_detail_page.edit_status_label")}
                  </label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t("status.active")}</SelectItem>
                      <SelectItem value="paused">{t("status.paused")}</SelectItem>
                      <SelectItem value="done">{t("status.done")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("project_detail_page.edit_priority_label")}
                  </label>
                  <Select
                    value={priority || "__none__"}
                    onValueChange={(v) => setPriority(v === "__none__" ? "" : v)}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        {t("project_detail_page.edit_no_priority")}
                      </SelectItem>
                      <SelectItem value="high">{t("priority.high")}</SelectItem>
                      <SelectItem value="medium">{t("priority.medium")}</SelectItem>
                      <SelectItem value="low">{t("priority.low")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("project_detail_page.edit_deadline_label")}
                </label>
                <Input
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="sm:mr-auto text-destructive/70 hover:text-destructive hover:bg-destructive/10"
                onClick={() => setDeleteConfirm(true)}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                {t("project_detail_page.delete_btn")}
              </Button>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                {t("common.cancel")}
              </Button>
              <Button onClick={handleSave} disabled={update.isPending}>
                {update.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t("common.save")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TaskItem({ task, projectId }: { task: Task; projectId: string }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : fr;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const completeTask = useCompleteTask();
  const postponeTask = usePostponeTask();
  const [editOpen, setEditOpen] = useState(false);

  const isCompleted = task.status === "done";

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetProjectQueryKey(projectId) });
    queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
  };

  const handleComplete = () => {
    if (isCompleted) return;
    completeTask.mutate(
      { id: task.id },
      {
        onSuccess: () => {
          invalidate();
          toast({ title: t("project_detail_page.task_done_toast") });
        },
        onError: () => toast({ title: t("common.complete_error"), variant: "destructive" }),
      },
    );
  };

  const handlePostpone = () => {
    postponeTask.mutate(
      { id: task.id },
      {
        onSuccess: () => {
          invalidate();
          toast({ title: t("project_detail_page.task_postponed_toast") });
        },
        onError: () => toast({ title: t("common.postpone_error"), variant: "destructive" }),
      },
    );
  };

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-xl transition-colors border group ${
        isCompleted
          ? "opacity-50 border-transparent"
          : "border-transparent hover:border-border/50 hover:bg-secondary/40"
      }`}
    >
      <button
        type="button"
        onClick={handleComplete}
        disabled={isCompleted || completeTask.isPending}
        className={`mt-0.5 shrink-0 ${
          isCompleted ? "text-primary" : "text-muted-foreground hover:text-primary"
        } transition-colors`}
        aria-label={isCompleted ? t("project_detail_page.task_done_aria") : t("project_detail_page.task_mark_done_aria")}
      >
        {completeTask.isPending ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : isCompleted ? (
          <CheckCircle2 className="w-5 h-5" />
        ) : (
          <Circle className="w-5 h-5" />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p
            className={`font-medium ${
              isCompleted ? "line-through text-muted-foreground" : "text-foreground"
            }`}
          >
            {task.title}
          </p>
          <DelegationBadge task={task} />
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
          {task.estimatedMinutes != null && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> {task.estimatedMinutes} min
            </span>
          )}
          {task.dueDate && (
            <span className="flex items-center gap-1">
              <CalendarClock className="w-3 h-3" />
              {format(new Date(task.dueDate), "d MMM", { locale: dateLocale })}
            </span>
          )}
          {task.priority === "high" && (
            <span className="text-destructive font-medium uppercase tracking-wide text-[10px]">
              {t("priority.high_short")}
            </span>
          )}
        </div>
        {!isCompleted && (
          // Always-visible actions — touch-friendly. The completion circle
          // on the left already handles "Terminer".
          <div className="mt-2 -ml-2 flex items-center gap-1 flex-wrap">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs px-2.5 text-muted-foreground hover:text-foreground"
              onClick={() => setEditOpen(true)}
              data-testid="button-edit-task"
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" />
              {t("common.edit")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs px-2.5 text-muted-foreground hover:text-foreground"
              onClick={handlePostpone}
              disabled={postponeTask.isPending}
              data-testid="button-postpone-task"
            >
              {postponeTask.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <CalendarClock className="w-3.5 h-3.5 mr-1.5" />
              )}
              {t("common.postpone_tomorrow")}
            </Button>
            <TaskQuickActions task={task} />
          </div>
        )}
      </div>
      <TaskEditDialog
        task={task}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
    </div>
  );
}

function ClarifyNextAction({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const suggestion = useSuggestNextAction(projectId, {
    query: {
      queryKey: getSuggestNextActionQueryKey(projectId),
      enabled,
      staleTime: 60_000,
    },
  });
  const createTask = useCreateTask();

  const handleAccept = () => {
    if (!suggestion.data) return;
    createTask.mutate(
      {
        data: {
          title: suggestion.data.title,
          description: suggestion.data.description ?? null,
          projectId,
          priority: suggestion.data.priority ?? null,
          estimatedMinutes: suggestion.data.estimatedMinutes ?? null,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetProjectQueryKey(projectId) });
          queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
          toast({ title: t("project_detail_page.action_added_toast") });
          setEnabled(false);
        },
        onError: () => toast({ title: t("common.add_task_error"), variant: "destructive" }),
      },
    );
  };

  return (
    <Card className="rounded-2xl border-primary/20 bg-primary/5">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-foreground">{t("project_detail_page.clarify_title")}</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          {t("project_detail_page.clarify_desc")}
        </p>

        {enabled && suggestion.isFetching && (
          <div className="space-y-2 mb-3">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        )}

        {enabled && suggestion.error && !suggestion.isFetching && (
          <p className="text-sm text-destructive mb-3">
            {t("project_detail_page.suggestion_error")}
          </p>
        )}

        {enabled && suggestion.data && !suggestion.isFetching && (
          <div className="p-3 rounded-xl bg-card border border-border mb-3">
            <div className="text-sm font-medium text-foreground">{suggestion.data.title}</div>
            {suggestion.data.description && (
              <p className="text-xs text-muted-foreground mt-1">
                {suggestion.data.description}
              </p>
            )}
            {suggestion.data.reason && (
              <p className="text-xs text-muted-foreground mt-2 italic">
                {suggestion.data.reason}
              </p>
            )}
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2 flex-wrap">
              {suggestion.data.priority && (
                <span className="flex items-center gap-1">
                  <Flag className="w-3 h-3" />
                  {t(`priority.${suggestion.data.priority}`, suggestion.data.priority)}
                </span>
              )}
              {suggestion.data.estimatedMinutes != null && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {suggestion.data.estimatedMinutes} min
                </span>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setEnabled(true);
              void suggestion.refetch();
            }}
            disabled={suggestion.isFetching}
          >
            {suggestion.isFetching ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Lightbulb className="w-4 h-4 mr-2" />
            )}
            {suggestion.data ? t("project_detail_page.another_suggestion_btn") : t("project_detail_page.suggest_action_btn")}
          </Button>
          {suggestion.data && enabled && (
            <Button size="sm" onClick={handleAccept} disabled={createTask.isPending}>
              {createTask.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              {t("project_detail_page.add_task_btn")}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ProjectDetailPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : fr;
  const { id } = useParams();
  const { data: project, isLoading, error } = useGetProject(id || "");
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createTask = useCreateTask();

  if (!id) return null;

  const handleCreateTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;

    createTask.mutate(
      { data: { title: newTaskTitle, projectId: id } },
      {
        onSuccess: () => {
          setNewTaskTitle("");
          queryClient.invalidateQueries({ queryKey: getGetProjectQueryKey(id) });
          queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
        },
        onError: () => toast({ title: t("common.add_task_error"), variant: "destructive" }),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-8 max-w-3xl mx-auto space-y-6">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-4 w-full max-w-md" />
        <div className="space-y-4 mt-8">
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (error || !project) {
    return <div className="p-8 text-center text-destructive">{t("project_detail_page.not_found")}</div>;
  }

  const openTasks = project.tasks.filter(
    (t) => t.status !== "done" && t.status !== "archived" && t.status !== "refused",
  );
  const completedTasks = project.tasks.filter((t) => t.status === "done");
  const upcomingEvents = project.events.filter(
    (e) => e.startTime && new Date(e.startTime).getTime() >= Date.now() - 24 * 3600 * 1000,
  );

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto pb-24 md:pb-8">
      <Button variant="ghost" size="sm" asChild className="mb-6 -ml-3 text-muted-foreground">
        <Link href="/app/projects">
          <ChevronLeft className="w-4 h-4 mr-1" /> {t("project_detail_page.back_btn")}
        </Link>
      </Button>

      <header className="mb-8">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{project.name}</h1>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 mt-1 text-muted-foreground hover:text-foreground"
            onClick={() => setEditOpen(true)}
            aria-label={t("project_detail_page.edit_btn_aria")}
          >
            <Pencil className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <Badge variant="outline">{t(`status.${project.status}`, project.status)}</Badge>
          {project.priority && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Flag className="w-3 h-3" />
              {t(`priority.${project.priority}`, project.priority)}
            </Badge>
          )}
          {project.deadline && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <CalendarClock className="w-3 h-3" />
              {format(new Date(project.deadline), "d MMM yyyy", { locale: dateLocale })}
            </span>
          )}
        </div>
        {project.description && (
          <p className="text-muted-foreground text-lg">{project.description}</p>
        )}
      </header>

      <ProjectEditDialog
        project={{
          id: project.id,
          name: project.name,
          description: project.description ?? null,
          status: project.status,
          priority: project.priority ?? null,
          deadline: project.deadline ?? null,
        }}
        open={editOpen}
        onOpenChange={setEditOpen}
      />

      <div className="space-y-8">
        <ClarifyNextAction projectId={project.id} />

        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center justify-between">
            {t("project_detail_page.open_tasks_title")}
            <span className="bg-secondary text-secondary-foreground text-xs px-2 py-1 rounded-full font-mono">
              {openTasks.length}
            </span>
          </h2>

          <div className="space-y-1 mb-4">
            {openTasks.length === 0 ? (
              <p className="text-muted-foreground text-sm italic py-4 px-2">
                {t("project_detail_page.open_tasks_empty")}
              </p>
            ) : (
              openTasks.map((task) => (
                <TaskItem key={task.id} task={task} projectId={project.id} />
              ))
            )}
          </div>

          <form onSubmit={handleCreateTask} className="flex gap-2 items-center relative">
            <Plus className="w-4 h-4 absolute left-3 text-muted-foreground pointer-events-none" />
            <Input
              placeholder={t("project_detail_page.new_task_placeholder")}
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              className="pl-9 bg-secondary/20 border-transparent focus-visible:bg-background rounded-xl h-12"
              disabled={createTask.isPending}
            />
            {createTask.isPending && (
              <Loader2 className="w-4 h-4 animate-spin absolute right-3 text-muted-foreground" />
            )}
          </form>
        </section>

        {completedTasks.length > 0 && (
          <section className="pt-6 border-t border-border/50">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              {t("project_detail_page.completed_tasks_title", { count: completedTasks.length })}
            </h2>
            <div className="space-y-1">
              {completedTasks.map((task) => (
                <TaskItem key={task.id} task={task} projectId={project.id} />
              ))}
            </div>
          </section>
        )}

        <NotesSection projectId={project.id} />
        <FilesSection projectId={project.id} />
        <ProjectTimeline projectId={project.id} tasks={project.tasks} />

        {upcomingEvents.length > 0 && (
          <section className="pt-6 border-t border-border/50">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-primary" />
              {t("project_detail_page.events_title")}
            </h2>
            <div className="grid gap-3">
              {upcomingEvents.map((ev) => (
                <Card key={ev.id} className="rounded-xl">
                  <CardContent className="p-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-foreground">{ev.title}</div>
                      {ev.startTime && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {format(new Date(ev.startTime), "EEEE d MMM HH:mm", { locale: dateLocale })}
                        </div>
                      )}
                    </div>
                    <Button
                      asChild
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                    >
                      <a
                        href={`/api/events/${ev.id}/ics`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t("project_detail_page.download_ics")}
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {project.inboxItems.length > 0 && (
          <section className="pt-6 border-t border-border/50">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <InboxIcon className="w-5 h-5 text-primary" />
              {t("project_detail_page.captures_title")}
            </h2>
            <div className="grid gap-2 min-w-0 overflow-hidden">
              {project.inboxItems.map((item) => {
                const emailParsed =
                  item.sourceType === "email" ? parseEmailRawText(item.rawText) : null;
                if (emailParsed) {
                  return (
                    <div
                      key={item.id}
                      className="p-3 rounded-xl bg-secondary/30 border border-border overflow-hidden min-w-0"
                    >
                      {emailParsed.subject && (
                        <div className="text-sm font-medium text-foreground truncate">
                          {emailParsed.subject}
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground min-w-0">
                        {emailParsed.fromName && (
                          <span className="truncate">{emailParsed.fromName}</span>
                        )}
                        {emailParsed.fromName && <span className="shrink-0">·</span>}
                        <span className="shrink-0">
                          {formatDistanceToNow(new Date(item.createdAt), {
                            addSuffix: true,
                            locale: dateLocale,
                          })}
                        </span>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={item.id}
                    className="p-3 rounded-xl bg-secondary/30 border border-border text-sm text-muted-foreground line-clamp-2 overflow-hidden min-w-0"
                  >
                    {item.rawText || item.transcriptText || t("project_detail_page.capture_empty")}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
